import { useState } from 'react';
import referralService from '../../../services/referral';

interface DocumentActionDialogState {
  open: boolean;
  documentId: number | null;
  action: 'retry' | 'resolve' | null;
  reason: string;
  loading: boolean;
}

export const useDocumentActions = (onSuccess?: () => void) => {
  const [dialogState, setDialogState] = useState<DocumentActionDialogState>({
    open: false,
    documentId: null,
    action: null,
    reason: '',
    loading: false,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const handleRetryProcessing = (documentId: number) => {
    setDialogState({
      open: true,
      documentId,
      action: 'retry',
      reason: '',
      loading: false,
    });
  };

  const handleMarkResolved = (documentId: number) => {
    setDialogState({
      open: true,
      documentId,
      action: 'resolve',
      reason: '',
      loading: false,
    });
  };

  const handleDialogClose = () => {
    if (!dialogState.loading) {
      setDialogState({
        open: false,
        documentId: null,
        action: null,
        reason: '',
        loading: false,
      });
    }
  };

  const handleReasonChange = (reason: string) => {
    setDialogState((prev) => ({ ...prev, reason }));
  };

  const handleConfirmAction = async () => {
    if (!dialogState.documentId || !dialogState.action) return;

    setDialogState((prev) => ({ ...prev, loading: true }));

    try {
      let response;
      if (dialogState.action === 'retry') {
        response = await referralService.retryDocumentProcessing(
          dialogState.documentId,
          dialogState.reason || undefined
        );
      } else {
        if (!dialogState.reason.trim()) {
          setSnackbar({
            open: true,
            message: 'Please provide a reason for marking the document as resolved.',
            severity: 'error',
          });
          setDialogState((prev) => ({ ...prev, loading: false }));
          return;
        }
        response = await referralService.markDocumentAsResolved(
          dialogState.documentId,
          dialogState.reason
        );
      }

      if (response.success) {
        setSnackbar({
          open: true,
          message: response.message,
          severity: 'success',
        });
        handleDialogClose();
        if (onSuccess) onSuccess();
      } else {
        setSnackbar({
          open: true,
          message: response.message,
          severity: 'error',
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'An error occurred while processing the request.',
        severity: 'error',
      });
    } finally {
      setDialogState((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return {
    dialogState,
    snackbar,
    handleRetryProcessing,
    handleMarkResolved,
    handleDialogClose,
    handleReasonChange,
    handleConfirmAction,
    handleSnackbarClose,
  };
};